// externals
import { logNewRelicPageAction } from '@makemydeal/dr-activities-common';
import { offerSessionUtils } from '@makemydeal/dr-shared-ui-utils';
import React from 'react';

// interstate components
import { Alert } from '@interstate/components/Alert';
import { Button } from '@interstate/components/Button';

/**
 * Stable New Relic event names emitted by the Common Consumer Card error boundary.
 * Keep these names stable — they are referenced by NRQL queries on the
 * "MV — Common Consumer Card Integration Health" dashboard (awsdrst account).
 */
export const REMOTE_MODULE_ERROR_EVENT = 'MV:CCC:ModuleRenderError';

export interface RemoteModuleErrorBoundaryProps {
    /**
     * Logical name of the federated module being rendered (e.g. `CustomerSidebarModule`).
     * Used as a NR attribute to slice failure rates per module.
     */
    moduleName: string;
    /**
     * Common consumer id in scope (when available). Helps debug a render error
     * by correlating it back to the specific shopper that triggered it.
     */
    commonConsumerId?: string;
    /**
     * Dealer id in scope (when available). Forwarded to New Relic so MV:CCC:*
     * events on the "MV — Common Consumer Card Integration Health" dashboard
     * can be sliced / drilled-into per dealer. Sourced from Redux by the
     * parent component (boundary is a class component and cannot use hooks).
     */
    dealerId?: string;
    /**
     * Optional override for the fallback UI displayed when the boundary catches an error.
     * Defaults to an Interstate <Alert> with a Retry button.
     */
    fallback?: React.ReactNode;
    children: React.ReactNode;
}

interface RemoteModuleErrorBoundaryState {
    hasError: boolean;
    error: Error | null;
}

/**
 * React error boundary used to wrap the lazy-loaded Common Consumer Card remote
 * modules (CustomerSidebarModule, FindOrCreateModule, CustomerEditModule).
 *
 * Without this boundary, render-time exceptions thrown from inside the federated
 * remote bubble up uncaught and leave the user with a blank drawer. This boundary
 * surfaces a friendly fallback UI and emits a `MV:CCC:ModuleRenderError` page
 * action to New Relic so the team can detect and triage CCC PBC failures.
 */
export class RemoteModuleErrorBoundary extends React.Component<RemoteModuleErrorBoundaryProps, RemoteModuleErrorBoundaryState> {
    constructor(props: RemoteModuleErrorBoundaryProps) {
        super(props);
        this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error: Error): RemoteModuleErrorBoundaryState {
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
        const { moduleName, commonConsumerId, dealerId } = this.props;
        logNewRelicPageAction(REMOTE_MODULE_ERROR_EVENT, {
            environment: offerSessionUtils.getEnvironmentType(),
            moduleName,
            commonConsumerId: commonConsumerId ?? '',
            dealerId: dealerId ?? '',
            errorMessage: error.message,
            componentStack: errorInfo.componentStack
        });
    }

    handleRetry = () => {
        this.setState({ hasError: false, error: null });
    };

    render() {
        if (this.state.hasError) {
            if (this.props.fallback !== undefined) {
                return this.props.fallback;
            }
            return (
                <Alert
                    data-testid={`remote-module-error-${this.props.moduleName}`}
                    title="Customer panel failed to load"
                    type="error"
                    role="alert"
                >
                    Please try again. If the problem persists, contact support.
                    <div style={{ marginTop: '0.5rem' }}>
                        <Button
                            data-testid={`remote-module-error-retry-${this.props.moduleName}`}
                            buttonStyle="secondary"
                            size="small"
                            onClick={this.handleRetry}
                        >
                            Retry
                        </Button>
                    </div>
                </Alert>
            );
        }
        return this.props.children;
    }
}

export default RemoteModuleErrorBoundary;
